// Reglas de la pantalla Control MDM: presentación del estado, heartbeat, filtros, orden y
// elegibilidad de las acciones. Puras y sin React — los componentes solo las pintan.
import { LoanDeviceLockReasonEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceLockReasonEnum';
import { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { MdmDeviceStatusEnum } from '@fiado/type-kit/bin/mdm/enums/MdmDeviceStatusEnum';
import { MdmOperationStatusEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationStatusEnum';
import { DeviceReleaseReasonEnum } from '@fiado/type-kit/bin/mdm/enums/DeviceReleaseReasonEnum';
import { MdmOperationTypeEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationTypeEnum';
import { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import { MdmLockModeEnum } from '@fiado/type-kit/bin/retailCatalog/enums/MdmLockModeEnum';
import { daysBetweenMx, formatTimeMx } from '@/lib/dateHelpers';
import { wireEnum } from '@/lib/wireEnum';
import {
    ANY_FILTER,
    type HeartbeatBucket,
    type HeartbeatReading,
    type HeartbeatSeverity,
    type MdmFleetDevice,
    type MdmFleetFilters,
    type MdmDeviceSituation,
    type MdmFleetSummary,
    type MdmStatusPresentation,
    type MdmStatusTone,
    type MdmSuggestedAction,
} from './types';
import { wireLookup } from '@/lib/wireLookup';
import { closeSentence } from '@/shared/patterns/LoadedScope';

/** Lo que se pinta donde el expediente no tiene dato: em dash, nunca vacío ni «N/A». */
export const NO_VALUE = '—';

/** Concuerda el sustantivo con la cuenta. Nada de «día(s)»: el paréntesis no lo lee nadie. */
export function countLabel(total: number, singular: string, plural: string): string {
    return `${String(total)} ${total === 1 ? singular : plural}`;
}

/** «1 equipo» / «3 equipos»: la cuenta que comparten los KPIs y los rótulos de la pantalla. */
export function deviceCountLabel(total: number): string {
    return countLabel(total, 'equipo', 'equipos');
}

/** «1 crédito» / «2 créditos»: la cuenta del deep link con el IMEI repetido. */
export function creditCountLabel(total: number): string {
    return countLabel(total, 'crédito', 'créditos');
}

export function dayCountLabel(total: number): string {
    return countLabel(total, 'día', 'días');
}

export function callCountLabel(total: number): string {
    return countLabel(total, 'llamada', 'llamadas');
}

/** «1 operación» / «2 operaciones»: la cuenta de la card de situación del proveedor. */
export function operationCountLabel(total: number): string {
    return countLabel(total, 'operación', 'operaciones');
}

/** La grafía buena del proveedor: la doc de SureKeep la escribe así, el mock se contradice solo. */
export const MDM_PROVIDER_LABEL: Record<MdmProviderEnum, string> = {
    [MdmProviderEnum.TRUSTONIC]: 'Trustonic',
    [MdmProviderEnum.DATACULTR]: 'Datacultr',
    [MdmProviderEnum.MOTOSAFE]: 'Motosafe',
};

/** El modo de bloqueo pactado con el proveedor, como se lee dentro de una frase. */
export const MDM_LOCK_MODE_LABEL: Record<MdmLockModeEnum, string> = {
    [MdmLockModeEnum.ONDEMAND]: 'bloqueo bajo demanda',
    [MdmLockModeEnum.SCHEDULED]: 'bloqueo programado',
};

/**
 * El mismo modo como etiqueta suelta de columna o métrica. Es un Record aparte y no una derivación
 * del anterior: recortarlo daría «Bloqueo bajo demanda», que no entra en la columna.
 */
export const MDM_LOCK_MODE_SHORT_LABEL: Record<MdmLockModeEnum, string> = {
    [MdmLockModeEnum.ONDEMAND]: 'Bajo demanda',
    [MdmLockModeEnum.SCHEDULED]: 'Programado',
};

/** Lo que se dice donde el expediente no guarda con qué modo se enroló el equipo. */
export const MISSING_LOCK_MODE_NOTE =
    'El expediente no registra con qué modo se enroló este equipo.';

/**
 * `Datacultr · bloqueo programado`. El modo se omite donde no aplica en vez de pintar un guion: en
 * el programado un unlock manual no cancela el autolock, y eso cambia lo que el operador hace.
 */
export function providerContextLabel(device: MdmFleetDevice): string {
    const provider = MDM_PROVIDER_LABEL[device.mdmProvider];
    return device.mdmLockMode
        ? `${provider} · ${MDM_LOCK_MODE_LABEL[device.mdmLockMode]}`
        : provider;
}

/** Lo que se dice en lugar de la bitácora cuando el rol no la puede leer. */
export const NO_AUDIT_PERMISSION = 'No tienes el permiso para ver la bitácora de operaciones.';

/** Cómo se lee cada observación del proveedor dentro de una frase en español. */
export const MDM_PROVIDER_STATUS_LABEL: Record<MdmDeviceStatusEnum, string> = {
    [MdmDeviceStatusEnum.NOT_ENROLLED]: 'sin enrolar',
    [MdmDeviceStatusEnum.ENROLLMENT_PENDING]: 'con el enrolamiento pendiente',
    [MdmDeviceStatusEnum.ACTIVE]: 'activo',
    [MdmDeviceStatusEnum.NOTIFIED]: 'con el aviso ya enviado',
    [MdmDeviceStatusEnum.APPS_RESTRICTED]: 'con apps restringidas',
    [MdmDeviceStatusEnum.LOCKED]: 'bloqueado',
    [MdmDeviceStatusEnum.RELEASED]: 'fuera del MDM',
    [MdmDeviceStatusEnum.UNKNOWN]: 'en estado desconocido',
};

/** El nombre de cada operación del catálogo, para el copy en español de las cards. */
export const MDM_OPERATION_LABEL: Record<MdmOperationTypeEnum, string> = {
    [MdmOperationTypeEnum.STATUS]: 'Consulta de estado',
    [MdmOperationTypeEnum.LAST_SEEN]: 'Consulta de última señal',
    [MdmOperationTypeEnum.PIN_UNLOCK]: 'Desbloqueo con PIN',
    [MdmOperationTypeEnum.LOCK]: 'Bloqueo',
    [MdmOperationTypeEnum.UNLOCK]: 'Desbloqueo',
    [MdmOperationTypeEnum.NOTIFY]: 'Aviso al equipo',
    [MdmOperationTypeEnum.ENROLL]: 'Enrolamiento',
    [MdmOperationTypeEnum.RELEASE]: 'Liberación del MDM',
    [MdmOperationTypeEnum.ARCHIVE]: 'Archivado',
    [MdmOperationTypeEnum.DEACTIVATE]: 'Desactivación',
    [MdmOperationTypeEnum.RESTRICT_APPS]: 'Restricción de apps',
    [MdmOperationTypeEnum.AUTO_LOCK_SCHEDULE]: 'Programación de autobloqueo',
    [MdmOperationTypeEnum.LOG]: 'Registro en bitácora',
};

/** Cómo se lee cada motivo de liberación en el combo del diálogo y en la bitácora. */
export const MDM_RELEASE_REASON_LABEL: Record<DeviceReleaseReasonEnum, string> = {
    [DeviceReleaseReasonEnum.ACCOUNTING_WRITE_OFF]: 'Castigo contable',
    [DeviceReleaseReasonEnum.CREDIT_PAID_OFF]: 'Crédito liquidado',
    [DeviceReleaseReasonEnum.OTHER]: 'Otro motivo',
};

/** El orden en el que el combo los pinta: alfabético por etiqueta, no el del enum. */
export const MDM_RELEASE_REASONS_ALPHABETICAL: readonly DeviceReleaseReasonEnum[] = Object.values(
    DeviceReleaseReasonEnum,
).sort((a, b) => MDM_RELEASE_REASON_LABEL[a].localeCompare(MDM_RELEASE_REASON_LABEL[b], 'es-MX'));

/** Cómo se pinta el desenlace de una llamada en la bitácora. */
export interface MdmOperationStatusPresentation {
    label: string;
    /** Lo mismo con todas sus letras, para el tooltip de la celda angosta. */
    title: string;
    toneClass: string;
}

/** Los tres desenlaces que el log guarda. Es lo único que la tabla sabe de cómo terminó la llamada. */
export const MDM_OPERATION_STATUS_PRESENTATION: Record<
    MdmOperationStatusEnum,
    MdmOperationStatusPresentation
> = {
    [MdmOperationStatusEnum.SUCCESS]: {
        label: 'Éxito',
        title: 'El proveedor aceptó la llamada',
        toneClass: 'border-success/40 bg-success/10 text-success',
    },
    [MdmOperationStatusEnum.PENDING]: {
        label: 'Pendiente',
        title: 'La operación sigue abierta: el proveedor todavía no la cerró',
        toneClass: 'border-warning/40 bg-warning/10 text-warning',
    },
    [MdmOperationStatusEnum.ERROR]: {
        label: 'Error',
        title: 'El proveedor rechazó la llamada o falló al atenderla',
        toneClass: 'border-destructive/40 bg-destructive/10 text-destructive',
    },
};

/** Un desenlace fuera del enum: la respuesta no se valida en runtime y no hay nada que afirmar. */
const UNKNOWN_OPERATION_STATUS: MdmOperationStatusPresentation = {
    label: 'Desconocido',
    title: 'La bitácora guardó un desenlace que esta pantalla no reconoce',
    toneClass: 'border-border bg-muted text-muted-foreground',
};

/** Cómo pintar el desenlace de una llamada, tolerando un `status` que el enum no tenga. */
export function resolveOperationStatusPresentation(
    status: MdmOperationStatusEnum,
): MdmOperationStatusPresentation {
    const byStatus: Partial<Record<MdmOperationStatusEnum, MdmOperationStatusPresentation>> =
        MDM_OPERATION_STATUS_PRESENTATION;
    return byStatus[status] ?? UNKNOWN_OPERATION_STATUS;
}

/**
 * Cómo se pinta cada estado del expediente, con la etiqueta larga y la corta de la tabla. El Record
 * es completo a propósito: sumar un valor al enum rompe la compilación en vez de caer en un default.
 */
export const MDM_STATUS_PRESENTATION: Record<LoanDeviceMdmStatusEnum, MdmStatusPresentation> = {
    [LoanDeviceMdmStatusEnum.NOT_ENROLLED]: {
        label: 'Sin enrolar',
        shortLabel: 'Sin enrolar',
        tone: 'neutral',
        needsAttention: false,
    },
    [LoanDeviceMdmStatusEnum.ENROLLING]: {
        label: 'Enrolando',
        shortLabel: 'Enrolando',
        tone: 'info',
        needsAttention: false,
    },
    [LoanDeviceMdmStatusEnum.ACTIVE]: {
        label: 'Activo',
        shortLabel: 'Activo',
        tone: 'success',
        needsAttention: false,
    },
    [LoanDeviceMdmStatusEnum.LOCKED]: {
        label: 'Bloqueado',
        shortLabel: 'Bloqueado',
        tone: 'danger',
        needsAttention: false,
    },
    [LoanDeviceMdmStatusEnum.RELEASED]: {
        label: 'Liberado del MDM',
        shortLabel: 'Liberado del MDM',
        tone: 'neutral',
        needsAttention: false,
    },
    [LoanDeviceMdmStatusEnum.ARCHIVED]: {
        label: 'Archivado',
        shortLabel: 'Archivado',
        tone: 'neutral',
        needsAttention: false,
    },
    [LoanDeviceMdmStatusEnum.FAILED]: {
        label: 'Enrolamiento falló',
        shortLabel: 'Enrolamiento falló',
        tone: 'warning',
        needsAttention: true,
    },
    [LoanDeviceMdmStatusEnum.FAILED_NOT_FOUND]: {
        label: 'IMEI desconocido para el proveedor',
        shortLabel: 'IMEI desconocido',
        tone: 'warning',
        needsAttention: true,
    },
    [LoanDeviceMdmStatusEnum.MANUAL_REVIEW]: {
        label: 'Requiere revisión manual',
        shortLabel: 'Revisión manual',
        tone: 'warning',
        needsAttention: true,
    },
};

export function getStatusPresentation(status: `${LoanDeviceMdmStatusEnum}`): MdmStatusPresentation {
    // Un estado nuevo del backend se muestra crudo y neutro en vez de tumbar la pantalla.
    return (
        wireLookup(MDM_STATUS_PRESENTATION, status) ?? {
            label: status,
            shortLabel: status,
            tone: 'neutral',
            needsAttention: false,
        }
    );
}

/**
 * Los 9 estados en el orden del ciclo de vida del equipo, con los tres de falla al final. El filtro
 * los ofrece así y no alfabéticos: quien busca en la lista busca una etapa, no una letra.
 */
export const MDM_STATUS_LIFECYCLE_ORDER: readonly LoanDeviceMdmStatusEnum[] = [
    LoanDeviceMdmStatusEnum.NOT_ENROLLED,
    LoanDeviceMdmStatusEnum.ENROLLING,
    LoanDeviceMdmStatusEnum.ACTIVE,
    LoanDeviceMdmStatusEnum.LOCKED,
    LoanDeviceMdmStatusEnum.RELEASED,
    LoanDeviceMdmStatusEnum.ARCHIVED,
    LoanDeviceMdmStatusEnum.FAILED,
    LoanDeviceMdmStatusEnum.FAILED_NOT_FOUND,
    LoanDeviceMdmStatusEnum.MANUAL_REVIEW,
];

/** Estados de los que ya no se vuelve: la UI apaga las operaciones y deja solo las consultas. */
export const TERMINAL_MDM_STATUSES: readonly LoanDeviceMdmStatusEnum[] = [
    LoanDeviceMdmStatusEnum.RELEASED,
    LoanDeviceMdmStatusEnum.ARCHIVED,
];

export function isTerminalStatus(status: LoanDeviceMdmStatusEnum): boolean {
    return TERMINAL_MDM_STATUSES.includes(status);
}

/** Estados donde el enrolamiento ya arrancó pero el proveedor todavía no lo confirmó. */
const UNCONFIRMED_ENROLLMENT_STATUSES: readonly LoanDeviceMdmStatusEnum[] = [
    LoanDeviceMdmStatusEnum.ENROLLING,
    LoanDeviceMdmStatusEnum.MANUAL_REVIEW,
];

export function hasUnconfirmedEnrollment(status: LoanDeviceMdmStatusEnum): boolean {
    return UNCONFIRMED_ENROLLMENT_STATUSES.includes(status);
}

/** Qué proveedores reportan la última señal del equipo. Motosafe no la reporta nunca. */
export const MDM_PROVIDER_REPORTS_HEARTBEAT: Record<MdmProviderEnum, boolean> = {
    [MdmProviderEnum.DATACULTR]: true,
    [MdmProviderEnum.MOTOSAFE]: false,
    [MdmProviderEnum.TRUSTONIC]: true,
};

/** Un proveedor que el front no conoce se trata como uno que sí reporta: su vacío sigue alarmando. */
export function reportsHeartbeat(provider: string): boolean {
    return wireLookup(MDM_PROVIDER_REPORTS_HEARTBEAT, provider) ?? true;
}

/** «Motosafe no reporta señal»: el vacío de un proveedor que no reporta, sin tono de alarma. */
function notReportedLabel(provider: string): string {
    return `${wireLookup(MDM_PROVIDER_LABEL, provider) ?? provider} no reporta señal`;
}

/** Motivo corto del guion de la tabla, donde solo cabe el tooltip. */
export function summarizeMissingHeartbeat(
    status: LoanDeviceMdmStatusEnum,
    provider: string,
): string {
    if (!reportsHeartbeat(provider)) return notReportedLabel(provider);
    return hasUnconfirmedEnrollment(status) ? 'Enrolamiento sin confirmar' : 'Nunca reportó';
}

/**
 * El mismo motivo con todas sus letras, para la ficha. Dice qué falta, nunca por qué: un equipo
 * con el enrolamiento sin confirmar sí puede estar reportando.
 */
export function explainMissingHeartbeat(
    status: `${LoanDeviceMdmStatusEnum}`,
    provider: string,
): string {
    if (!reportsHeartbeat(provider)) {
        return `${notReportedLabel(provider)}: el dato queda vacío por diseño y no indica una falla.`;
    }
    return hasUnconfirmedEnrollment(wireEnum<LoanDeviceMdmStatusEnum>(status))
        ? 'Enrolamiento sin confirmar: de este equipo todavía no llega ninguna señal.'
        : 'Nunca reportó: este equipo no tiene ninguna señal registrada.';
}

/** Por qué la ficha no tiene fecha de enrolamiento que mostrar. */
export function explainMissingEnrollmentDate(status: `${LoanDeviceMdmStatusEnum}`): string {
    return hasUnconfirmedEnrollment(wireEnum<LoanDeviceMdmStatusEnum>(status))
        ? 'La fecha se sella cuando el proveedor confirma el enrolamiento.'
        : 'El enrolamiento nunca se completó: no hay fecha que sellar.';
}

const HEARTBEAT_SEVERITY: Record<HeartbeatBucket, HeartbeatSeverity> = {
    'up-to-date': 'ok',
    recent: 'ok',
    stale: 'warn',
    critical: 'danger',
    unknown: 'neutral',
    'not-reported': 'neutral',
};

function bucketForDays(days: number): HeartbeatBucket {
    if (days < 1) return 'up-to-date';
    if (days <= 3) return 'recent';
    if (days <= 7) return 'stale';
    return 'critical';
}

/**
 * Antigüedad del heartbeat en días de CALENDARIO, que es como se lee al lado de la fecha.
 * `now` entra por parámetro para que el cálculo sea determinista.
 */
export function readHeartbeat(
    lastSeenAt: string | undefined,
    now: Date | string,
    provider: string,
): HeartbeatReading {
    if (!lastSeenAt && !reportsHeartbeat(provider)) {
        return {
            bucket: 'not-reported',
            severity: HEARTBEAT_SEVERITY['not-reported'],
            label: NO_VALUE,
            longLabel: notReportedLabel(provider),
        };
    }
    if (!lastSeenAt) {
        return {
            bucket: 'unknown',
            severity: HEARTBEAT_SEVERITY.unknown,
            label: NO_VALUE,
            longLabel: 'Sin señal',
        };
    }
    const days = daysBetweenMx(lastSeenAt, now);
    const bucket = bucketForDays(days);
    return {
        bucket,
        severity: HEARTBEAT_SEVERITY[bucket],
        label: days < 1 ? 'Hoy' : `${String(days)}d`,
        longLabel: days < 1 ? 'Hoy' : `hace ${dayCountLabel(days)}`,
        daysSinceLastSeen: days,
    };
}

function normalize(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** La caja de búsqueda barre los seis campos que el operador tiene enfrente en la lista. */
function matchesSearch(device: MdmFleetDevice, search: string): boolean {
    const needle = normalize(search);
    if (!needle) return true;
    const haystack = [
        device.imei,
        device.creditRef,
        device.customerId,
        device.customerName,
        device.brand,
        device.model,
        device.productSku,
    ];
    return haystack.some((field) => field !== undefined && normalize(field).includes(needle));
}

/** Cuántos filtros están puestos, para el conteo del encabezado de la columna. */
export function countActiveFilters(filters: MdmFleetFilters): number {
    const picked = [
        filters.state,
        filters.attention,
        filters.provider,
        filters.heartbeat,
        filters.brand,
    ].filter((value) => value !== ANY_FILTER).length;
    return picked + (filters.search.trim() === '' ? 0 : 1);
}

export function filterFleet(
    devices: readonly MdmFleetDevice[],
    filters: MdmFleetFilters,
    now: Date | string,
): MdmFleetDevice[] {
    return devices.filter((device) => {
        if (filters.provider !== ANY_FILTER && device.mdmProvider !== filters.provider)
            return false;
        if (filters.state !== ANY_FILTER && readFleetState(device, now) !== filters.state)
            return false;
        if (filters.attention !== ANY_FILTER && !needsPersonAttention(device)) return false;
        if (filters.brand !== ANY_FILTER && device.brand !== filters.brand) return false;
        if (
            filters.heartbeat !== ANY_FILTER &&
            readHeartbeat(device.lastSeenAt, now, device.mdmProvider).bucket !== filters.heartbeat
        ) {
            return false;
        }
        return matchesSearch(device, filters.search);
    });
}

/**
 * Marcas presentes en el fleet, en orden alfabético (el default de todo selector).
 *
 * Los equipos sin marca no aportan opción: el expediente MDM no la guarda, así que ofrecerla
 * llenaría el combo con una entrada que no distingue nada.
 */
export function listBrands(devices: readonly MdmFleetDevice[]): string[] {
    const brands = devices
        .map((device) => device.brand)
        .filter((brand): brand is string => brand !== undefined && brand !== '');
    return [...new Set(brands)].sort((a, b) => a.localeCompare(b, 'es-MX'));
}

/**
 * Proveedores presentes en la flota, en orden alfabético (el default de todo selector).
 *
 * Va sobre lo que HAY, no sobre el enum: ofrecer un proveedor sin un solo equipo manda al operador
 * a filtrar hacia una lista vacía.
 */
export function listProviders(devices: readonly MdmFleetDevice[]): MdmProviderEnum[] {
    const providers = [...new Set(devices.map((device) => device.mdmProvider))];
    return providers.sort((a, b) =>
        MDM_PROVIDER_LABEL[a].localeCompare(MDM_PROVIDER_LABEL[b], 'es-MX'),
    );
}

/** Lo que se pinta como «equipo»: marca y modelo si el portal los conoce, si no el SKU enrolado. */
export function deviceHardwareLabel(device: MdmFleetDevice): string {
    const brandModel = [device.brand, device.model].filter(Boolean).join(' ').trim();
    return brandModel || device.productSku || NO_VALUE;
}

/** Grupo de vista de una fila: cuanto más chico, más arriba va en la lista. */
const VIEW_GROUP = { operable: 0, silent: 1, terminal: 2 } as const;

/** Ubica al equipo en su grupo: terminal al fondo, y arriba de él lo que nunca reportó. */
function viewGroup(device: MdmFleetDevice, bucket: HeartbeatBucket): number {
    if (isTerminalStatus(device.mdmStatus)) return VIEW_GROUP.terminal;
    return bucket === 'unknown' ? VIEW_GROUP.silent : VIEW_GROUP.operable;
}

/**
 * Lo operable arriba, del heartbeat más viejo al más nuevo; después lo que nunca reportó y al
 * fondo lo terminal. Estable — los empates conservan el orden de entrada.
 */
export function sortFleet(
    devices: readonly MdmFleetDevice[],
    now: Date | string,
): MdmFleetDevice[] {
    const rank = new Map<MdmFleetDevice, { group: number; staleness: number }>(
        devices.map((device) => {
            const heartbeat = readHeartbeat(device.lastSeenAt, now, device.mdmProvider);
            return [
                device,
                {
                    group: viewGroup(device, heartbeat.bucket),
                    staleness: heartbeat.daysSinceLastSeen ?? -1,
                },
            ];
        }),
    );
    const NO_RANK = { group: VIEW_GROUP.terminal, staleness: -1 };
    return [...devices].sort((a, b) => {
        const left = rank.get(a) ?? NO_RANK;
        const right = rank.get(b) ?? NO_RANK;
        return left.group - right.group || right.staleness - left.staleness;
    });
}

/**
 * La acción que la ficha resalta en cada estado. NINGUNO sugiere bloquear ni liberar: son
 * destructivas y esta pantalla no tiene el dato que las respaldaría —la mora, el cierre del
 * crédito—, así que la sugerencia no tendría con qué sostenerse.
 */
const SUGGESTED_ACTION: Record<LoanDeviceMdmStatusEnum, MdmSuggestedAction> = {
    [LoanDeviceMdmStatusEnum.NOT_ENROLLED]: null,
    [LoanDeviceMdmStatusEnum.ENROLLING]: null,
    [LoanDeviceMdmStatusEnum.ACTIVE]: null,
    [LoanDeviceMdmStatusEnum.LOCKED]: 'unlock',
    [LoanDeviceMdmStatusEnum.RELEASED]: null,
    [LoanDeviceMdmStatusEnum.ARCHIVED]: null,
    [LoanDeviceMdmStatusEnum.FAILED]: null,
    [LoanDeviceMdmStatusEnum.FAILED_NOT_FOUND]: null,
    [LoanDeviceMdmStatusEnum.MANUAL_REVIEW]: null,
};

/** La acción que la ficha resalta; en un bloqueo por fraude el desbloqueo deja de sugerirse. */
export function resolveSuggestedAction(device: MdmFleetDevice): MdmSuggestedAction {
    const suggested = SUGGESTED_ACTION[device.mdmStatus];
    if (suggested === 'unlock' && device.lockReason === LoanDeviceLockReasonEnum.FRAUD) return null;
    return suggested;
}

/** Qué le pasa al equipo, en una línea, para cada estado del expediente. */
const STATUS_HEADLINE: Record<LoanDeviceMdmStatusEnum, string> = {
    [LoanDeviceMdmStatusEnum.NOT_ENROLLED]:
        'El crédito existe pero el enrolamiento nunca arrancó: el equipo no tiene candado.',
    [LoanDeviceMdmStatusEnum.ENROLLING]:
        'Enrolamiento en curso, esperando la confirmación del proveedor.',
    [LoanDeviceMdmStatusEnum.ACTIVE]: 'Equipo activo y sin candado.',
    [LoanDeviceMdmStatusEnum.LOCKED]: 'Equipo bloqueado por Fiado.',
    [LoanDeviceMdmStatusEnum.RELEASED]:
        'Fuera del MDM de forma permanente: ya no se puede bloquear ni desbloquear. No es un desbloqueo, es el fin de la tenencia con Fiado.',
    [LoanDeviceMdmStatusEnum.ARCHIVED]: 'Archivado de forma permanente, fuera del fleet activo.',
    [LoanDeviceMdmStatusEnum.FAILED]: 'El proveedor rechazó el enrolamiento.',
    [LoanDeviceMdmStatusEnum.FAILED_NOT_FOUND]: 'El proveedor no reconoce este IMEI.',
    [LoanDeviceMdmStatusEnum.MANUAL_REVIEW]:
        'El enrolamiento lleva más de 24 horas trabado y necesita que lo revises tú.',
};

/** El titular del bloqueo depende del motivo: la regla de pago solo aplica a la mora. */
const LOCK_REASON_HEADLINE: Record<LoanDeviceLockReasonEnum, string> = {
    [LoanDeviceLockReasonEnum.OVERDUE_T_PLUS_1]:
        'Equipo bloqueado por cuota vencida. El cliente ve el mensaje de cobranza en su pantalla.',
    [LoanDeviceLockReasonEnum.ADMIN_MANUAL]:
        'Bloqueo manual de un administrador. Averigua por qué se puso antes de levantarlo.',
    [LoanDeviceLockReasonEnum.FRAUD]: 'Equipo bloqueado por fraude.',
};

/**
 * El porqué de cada estado, que respalda a la sugerencia. Cada razón la sostiene un hecho que la
 * pantalla SÍ conoce: el estado del expediente, el motivo del bloqueo o lo que el lambda acepta.
 */
const SITUATION_REASON: Record<LoanDeviceMdmStatusEnum, string> = {
    [LoanDeviceMdmStatusEnum.NOT_ENROLLED]:
        'El enrolamiento nunca arrancó, y desde esta pantalla no se lanza: lo dispara el motor de crédito al activar el crédito.',
    [LoanDeviceMdmStatusEnum.ENROLLING]:
        'El proveedor todavía no confirma el enrolamiento. Lo cierra el sondeo automático, no esta pantalla: actualizar te dice cómo lo ve el proveedor ahora.',
    [LoanDeviceMdmStatusEnum.ACTIVE]:
        'El equipo responde y no tiene candado. Si el cliente dejó de pagar, el corte lo pone la regla de cobranza, no esta pantalla.',
    [LoanDeviceMdmStatusEnum.LOCKED]:
        'El equipo está bloqueado por Fiado: si el cliente ya pagó, desbloquear es lo que lo libera.',
    [LoanDeviceMdmStatusEnum.RELEASED]:
        'El expediente llegó a su estado final: solo quedan las consultas.',
    [LoanDeviceMdmStatusEnum.ARCHIVED]:
        'El expediente llegó a su estado final: solo quedan las consultas.',
    [LoanDeviceMdmStatusEnum.FAILED]:
        'El proveedor rechazó el enrolamiento, y desde esta pantalla no se relanza: escálalo a soporte.',
    [LoanDeviceMdmStatusEnum.FAILED_NOT_FOUND]:
        'El proveedor no reconoce el IMEI; consultar el estado dice si sigue igual. Verifícalo con la tienda antes de insistir.',
    [LoanDeviceMdmStatusEnum.MANUAL_REVIEW]:
        'Lleva más de 24 horas trabado, y desde esta pantalla no se relanza: escálalo a soporte.',
};

/** Un bloqueo por fraude no se levanta con la regla de pago: por eso no sugiere nada. */
const FRAUD_LOCK_REASON =
    'Un bloqueo por fraude no se levanta con la regla de pago: desbloquearlo necesita autorización.';

const ACTIVE_UNCONFIRMED_HEADLINE =
    'Equipo activo sin confirmar: el proveedor todavía no confirma la última orden.';

const CUTOFF_PASSED_HEADLINE =
    'Corte vencido sin confirmar: pasó la hora del corte y el proveedor no dice si el equipo quedó bloqueado.';

const CUTOFF_REFUTED_HEADLINE =
    'Corte desmentido: el corte debía caer y el proveedor dice que el equipo sigue sin candado.';

/** Cómo se nombra el bloqueo en el titular de una orden sin confirmar, según su motivo. */
const LOCK_ORDER_NOUN: Record<LoanDeviceLockReasonEnum, string> = {
    [LoanDeviceLockReasonEnum.OVERDUE_T_PLUS_1]: 'Bloqueo por cuota vencida',
    [LoanDeviceLockReasonEnum.ADMIN_MANUAL]: 'Bloqueo manual',
    [LoanDeviceLockReasonEnum.FRAUD]: 'Bloqueo por fraude',
};

/** El titular de la orden que todavía no es candado: nunca afirma lo que ve el cliente. */
function lockOrderHeadline(noun: string, phase: MdmLockOrderPhase): string {
    switch (phase) {
        case 'scheduled':
            return `${noun} programado: el candado todavía no cae.`;
        case 'unconfirmed':
            return `${noun} sin confirmar: el proveedor no ha confirmado el candado.`;
        case 'refuted':
            return `${noun} desmentido: el proveedor dice que el equipo no tiene candado.`;
    }
}

function lockHeadline(device: MdmFleetDevice, now: Date | string): string {
    const reason = device.lockReason;
    const phase = readLockPhase(device, now);
    if (phase && phase !== 'locked' && !isCutoffPhase(phase)) {
        return lockOrderHeadline(reason ? LOCK_ORDER_NOUN[reason] : 'Bloqueo', phase);
    }
    return reason ? LOCK_REASON_HEADLINE[reason] : STATUS_HEADLINE[device.mdmStatus];
}

/** El porqué de sugerir desbloquear, según lo que el proveedor dijo del candado. */
const LOCKED_REASON_BY_PHASE: Record<MdmLockOrderPhase, string> = {
    scheduled:
        'La orden de bloqueo salió y el corte todavía no cae: el cliente sigue usando el equipo. Si ya pagó, desbloquear retira la orden.',
    unconfirmed:
        'La orden de bloqueo salió y esperamos que el proveedor confirme el candado. Si el cliente ya pagó, desbloquear retira la orden.',
    refuted:
        'El proveedor dice que el equipo no está bloqueado, aunque el expediente diga que sí. Si el cliente ya pagó, desbloquear retira la orden.',
};

function situationReason(device: MdmFleetDevice, now: Date | string): string {
    const phase = readLockPhase(device, now);
    if (phase === 'cutoffPassed') return CUTOFF_PASSED_NOTE;
    if (phase === 'cutoffRefuted') return CUTOFF_REFUTED_NOTE;
    if (device.mdmStatus !== LoanDeviceMdmStatusEnum.LOCKED)
        return SITUATION_REASON[device.mdmStatus];
    if (device.lockReason === LoanDeviceLockReasonEnum.FRAUD) return FRAUD_LOCK_REASON;
    // «Bloqueado por Fiado» solo con el candado confirmado; la orden dice lo que sabe.
    return phase && phase !== 'locked' && !isCutoffPhase(phase)
        ? LOCKED_REASON_BY_PHASE[phase]
        : SITUATION_REASON[LoanDeviceMdmStatusEnum.LOCKED];
}

function situationHeadline(device: MdmFleetDevice, now: Date | string): string {
    if (device.mdmStatus === LoanDeviceMdmStatusEnum.LOCKED) return lockHeadline(device, now);
    const phase = readLockPhase(device, now);
    if (phase === 'cutoffPassed') return CUTOFF_PASSED_HEADLINE;
    if (phase === 'cutoffRefuted') return CUTOFF_REFUTED_HEADLINE;
    return isActiveUnconfirmed(device)
        ? ACTIVE_UNCONFIRMED_HEADLINE
        : STATUS_HEADLINE[device.mdmStatus];
}

/** El corte vencido no se pinta con el verde de «Activo»; el desmentido va en rojo. */
function situationTone(device: MdmFleetDevice, now: Date | string): MdmStatusTone {
    const phase = readLockPhase(device, now);
    if (phase === 'cutoffRefuted') return 'danger';
    if (phase === 'cutoffPassed') return 'warning';
    return getStatusPresentation(device.mdmStatus).tone;
}

export function readDeviceSituation(
    device: MdmFleetDevice,
    now: Date | string,
): MdmDeviceSituation {
    return {
        headline: situationHeadline(device, now),
        suggested: resolveSuggestedAction(device),
        reason: situationReason(device, now),
        tone: situationTone(device, now),
    };
}

/** A qué equipo apunta un deep link. `ambiguous` = el IMEI está repartido en más de un crédito. */
export type MdmDeepLink =
    | { kind: 'none' }
    | { kind: 'device'; device: MdmFleetDevice }
    | { kind: 'ambiguous'; imei: string; devices: readonly MdmFleetDevice[] }
    | { kind: 'notFound'; imei: string | undefined; credit: string | undefined };

/** El equipo cuyo `creditRef` o `creditId` calza con el `?credit=` de la URL, sin distinguir mayúsculas. */
function findByCredit(
    devices: readonly MdmFleetDevice[],
    wantedCredit: string,
): MdmFleetDevice | undefined {
    return devices.find(
        (device) =>
            device.creditRef.toLowerCase() === wantedCredit ||
            device.creditId.toLowerCase() === wantedCredit,
    );
}

/**
 * Resuelve `?imei=` / `?credit=` al equipo que hay que abrir; `?imei=` manda sobre `?credit=`.
 * Un IMEI repartido en dos créditos solo queda `ambiguous` si el `?credit=` tampoco lo desempata.
 */
export function resolveDeepLink(
    devices: readonly MdmFleetDevice[],
    imei: string | undefined,
    credit: string | undefined,
): MdmDeepLink {
    const wantedImei = imei?.trim() || undefined;
    const rawCredit = credit?.trim() || undefined;
    const wantedCredit = rawCredit?.toLowerCase();
    if (wantedImei) {
        const matches = devices.filter((device) => device.imei === wantedImei);
        if (matches.length === 1) return { kind: 'device', device: matches[0] };
        if (matches.length > 1) {
            const disambiguated = wantedCredit ? findByCredit(matches, wantedCredit) : undefined;
            if (disambiguated) return { kind: 'device', device: disambiguated };
            return { kind: 'ambiguous', imei: wantedImei, devices: matches };
        }
    }
    if (!wantedImei && !wantedCredit) return { kind: 'none' };
    const match = wantedCredit ? findByCredit(devices, wantedCredit) : undefined;
    if (match) return { kind: 'device', device: match };
    return { kind: 'notFound', imei: wantedImei, credit: rawCredit };
}

/** Por qué la pantalla no abrió ninguna ficha: el servidor no tiene equipo para el deep link. */
export function explainDeepLinkNotFound(link: { imei?: string; credit?: string }): string {
    const target = link.imei ? `con IMEI ${link.imei}` : `del crédito ${link.credit ?? ''}`;
    return `No encontramos un equipo ${target}, así que no abrimos ninguno.`;
}

/** La búsqueda por IMEI que el servidor contestó vacía. */
export function explainImeiSearchEmpty(imei: string): string {
    return `No encontramos un equipo con IMEI ${imei}.`;
}

/** La búsqueda en el servidor se cayó: no es lo mismo que no encontrar el equipo. */
export function explainLookupFailed(message: string): string {
    return `No pudimos buscar el equipo: ${closeSentence(message)} Esto no significa que no exista; vuelve a intentarlo.`;
}

/** Por qué la pantalla no abrió ninguna ficha con ese `?imei=`: hay más de un crédito detrás. */
export function explainAmbiguousImei(imei: string, matches: number): string {
    return `El IMEI ${imei} está en ${creditCountLabel(matches)}. Elige en la lista cuál quieres abrir: esta pantalla no decide por ti.`;
}

/** Un equipo en estado terminal no admite operaciones (lock/unlock) desde la ficha. */
export function isOperable(device: MdmFleetDevice): boolean {
    return !isTerminalStatus(device.mdmStatus);
}

/** Los estados en los que el equipo está bajo control del MDM y acepta órdenes. */
const CONTROLLED_MDM_STATUSES: readonly LoanDeviceMdmStatusEnum[] = [
    LoanDeviceMdmStatusEnum.ACTIVE,
    LoanDeviceMdmStatusEnum.LOCKED,
];

/** `true` cuando el expediente está activo o bloqueado; cualquier otro estado no está bajo control. */
export function isUnderMdmControl(device: MdmFleetDevice): boolean {
    return CONTROLLED_MDM_STATUSES.includes(device.mdmStatus);
}

/** Fase del candado; `cutoffPassed`/`cutoffRefuted` = ACTIVE con el corte de este episodio vencido. */
export type MdmLockPhase =
    | 'refuted'
    | 'scheduled'
    | 'unconfirmed'
    | 'locked'
    | 'cutoffPassed'
    | 'cutoffRefuted';

/** Las fases del corte vencido de un equipo ACTIVE. */
export type MdmCutoffPhase = Extract<MdmLockPhase, 'cutoffPassed' | 'cutoffRefuted'>;

/** Las fases de una orden de bloqueo que todavía no es candado confirmado. */
export type MdmLockOrderPhase = Exclude<MdmLockPhase, 'locked' | MdmCutoffPhase>;

/** `true` para las dos fases del corte vencido de un ACTIVE. */
export function isCutoffPhase(phase: MdmLockPhase | null | undefined): phase is MdmCutoffPhase {
    return phase === 'cutoffPassed' || phase === 'cutoffRefuted';
}

/** Los campos del expediente que deciden la fase del candado. */
export type MdmLockPhaseFields = Pick<
    MdmFleetDevice,
    | 'mdmStatus'
    | 'mdmConfirmed'
    | 'lockRefutedByProvider'
    | 'currentLockScheduledFor'
    | 'unlockedAt'
    | 'mdmConfirmedAt'
>;

/** La hora del corte en milisegundos; `NaN` si no hay corte o no se puede leer. */
function cutoffMs(scheduledFor: string | undefined): number {
    return scheduledFor === undefined ? Number.NaN : new Date(scheduledFor).getTime();
}

/** `true` si el instante es posterior al corte; ausente o ilegible no es evidencia. */
function isAfterCutoff(at: string | undefined, fallsAt: number, inclusive: boolean): boolean {
    const atMs = cutoffMs(at);
    if (Number.isNaN(atMs)) return false;
    return inclusive ? atMs >= fallsAt : atMs > fallsAt;
}

/** El corte vencido de un ACTIVE: solo cuenta si es posterior al último desbloqueo. */
function readCutoffPhase(device: MdmLockPhaseFields, fallsAt: number): MdmCutoffPhase | null {
    if (isAfterCutoff(device.unlockedAt, fallsAt, true)) return null;
    if (device.lockRefutedByProvider) return 'cutoffRefuted';
    // Sin `unlockedAt` (API vieja), una confirmación posterior al corte es de otro episodio.
    const legacyApi = device.unlockedAt === undefined;
    if (legacyApi && device.mdmConfirmed && isAfterCutoff(device.mdmConfirmedAt, fallsAt, false))
        return null;
    return 'cutoffPassed';
}

export function readLockPhase(device: MdmLockPhaseFields, now: Date | string): MdmLockPhase | null {
    const fallsAt = cutoffMs(device.currentLockScheduledFor);
    const cutoffAhead = !Number.isNaN(fallsAt) && fallsAt > new Date(now).getTime();
    if (device.mdmStatus === LoanDeviceMdmStatusEnum.ACTIVE) {
        return Number.isNaN(fallsAt) || cutoffAhead ? null : readCutoffPhase(device, fallsAt);
    }
    if (device.mdmStatus !== LoanDeviceMdmStatusEnum.LOCKED) return null;
    // El desmentido manda: el expediente dice bloqueado y el equipo no tiene candado.
    if (device.lockRefutedByProvider) return 'refuted';
    if (device.mdmConfirmed) return 'locked';
    return cutoffAhead ? 'scheduled' : 'unconfirmed';
}

/** ACTIVE sin confirmar: la orden salió y el proveedor no la confirma; no es un equipo libre. */
export function isActiveUnconfirmed(
    device: Pick<MdmFleetDevice, 'mdmStatus' | 'mdmConfirmed'>,
): boolean {
    return device.mdmStatus === LoanDeviceMdmStatusEnum.ACTIVE && !device.mdmConfirmed;
}

/** La etiqueta del activo sin confirmar, para la píldora de la ficha y de la tabla. */
export const ACTIVE_UNCONFIRMED_LABEL = 'Activo · sin confirmar';

/** Lo que pinta la fila, cuenta el KPI y recorta el filtro «Estado»: una sola regla para los tres. */
export type MdmFleetState =
    | MdmLockPhase
    | 'activeUnconfirmed'
    | Exclude<LoanDeviceMdmStatusEnum, LoanDeviceMdmStatusEnum.LOCKED>;

export function readFleetState(device: MdmLockPhaseFields, now: Date | string): MdmFleetState {
    const phase = readLockPhase(device, now);
    if (phase) return phase;
    if (isActiveUnconfirmed(device)) return 'activeUnconfirmed';
    // Inalcanzable: todo LOCKED tiene fase. Se cubre para que el tipo cierre sin castear.
    if (device.mdmStatus === LoanDeviceMdmStatusEnum.LOCKED) return 'unconfirmed';
    return device.mdmStatus;
}

/** Los estados del filtro en el orden del ciclo de vida, con las fases del candado en su lugar. */
export const MDM_FLEET_STATE_ORDER: readonly MdmFleetState[] = [
    LoanDeviceMdmStatusEnum.NOT_ENROLLED,
    LoanDeviceMdmStatusEnum.ENROLLING,
    LoanDeviceMdmStatusEnum.ACTIVE,
    'activeUnconfirmed',
    'cutoffPassed',
    'cutoffRefuted',
    'scheduled',
    'unconfirmed',
    'refuted',
    'locked',
    LoanDeviceMdmStatusEnum.RELEASED,
    LoanDeviceMdmStatusEnum.ARCHIVED,
    LoanDeviceMdmStatusEnum.FAILED,
    LoanDeviceMdmStatusEnum.FAILED_NOT_FOUND,
    LoanDeviceMdmStatusEnum.MANUAL_REVIEW,
];

const LOCK_PHASES: readonly string[] = [
    'refuted',
    'scheduled',
    'unconfirmed',
    'locked',
    'cutoffPassed',
    'cutoffRefuted',
] satisfies MdmLockPhase[];

function isLockPhase(state: MdmFleetState): state is MdmLockPhase {
    return LOCK_PHASES.includes(state);
}

/** Cómo se llama cada estado en el filtro: la frase larga, porque «Sin confirmar» solo no distingue. */
export function fleetStateLabel(state: MdmFleetState): string {
    if (state === 'activeUnconfirmed') return ACTIVE_UNCONFIRMED_LABEL;
    if (isLockPhase(state)) return lockPhaseLabel(state, undefined);
    return MDM_STATUS_PRESENTATION[state].shortLabel;
}

/** Lo que tiene que ver una persona: una alerta viva del expediente o un enrolamiento trabado. */
export function needsPersonAttention(
    device: Pick<MdmFleetDevice, 'mdmStatus' | 'mdmAlerts'>,
): boolean {
    return (
        (device.mdmAlerts?.length ?? 0) > 0 ||
        getStatusPresentation(device.mdmStatus).needsAttention
    );
}

/** Forma corta del bloqueo que todavía no cae, para la columna angosta de la tabla. */
export const SCHEDULED_LOCK_SHORT_LABEL = 'Bloqueo programado';

/** Forma corta del corte que ya pasó sin evidencia, para la columna angosta de la tabla. */
export const CUTOFF_PASSED_SHORT_LABEL = 'Corte vencido';

/** Forma corta del corte que el proveedor desmintió: el equipo sigue sin candado. */
export const CUTOFF_REFUTED_SHORT_LABEL = 'Corte desmentido';

/** Cómo se llama cada fase en la columna angosta de la tabla; `locked` usa la del estado. */
export const MDM_LOCK_PHASE_SHORT_LABEL: Record<MdmLockPhase, string> = {
    refuted: 'Bloqueo desmentido',
    scheduled: SCHEDULED_LOCK_SHORT_LABEL,
    unconfirmed: 'Sin confirmar',
    locked: 'Bloqueado',
    cutoffPassed: CUTOFF_PASSED_SHORT_LABEL,
    cutoffRefuted: CUTOFF_REFUTED_SHORT_LABEL,
};

/** La frase larga de la fase, para la ficha y los tooltips; la corta es la de la columna. */
const MDM_LOCK_PHASE_LONG_LABEL: Record<Exclude<MdmLockPhase, 'scheduled'>, string> = {
    refuted: 'Bloqueo desmentido',
    unconfirmed: 'Orden de bloqueo sin confirmar',
    locked: 'Bloqueado',
    cutoffPassed: 'Corte vencido sin confirmar',
    cutoffRefuted: CUTOFF_REFUTED_SHORT_LABEL,
};

/** `Bloqueo programado · cae 12:34` para el corte que espera; la frase larga para las demás. */
export function lockPhaseLabel(phase: MdmLockPhase, scheduledFor: string | undefined): string {
    if (phase !== 'scheduled') return MDM_LOCK_PHASE_LONG_LABEL[phase];
    return scheduledFor
        ? `${SCHEDULED_LOCK_SHORT_LABEL} · cae ${formatTimeMx(scheduledFor)}`
        : SCHEDULED_LOCK_SHORT_LABEL;
}

/** Lo que el operador lee cuando pasó la hora del corte y el proveedor no dice si el candado cayó. */
export const CUTOFF_PASSED_NOTE =
    'Pasó la hora del corte y el proveedor no confirma si el equipo quedó bloqueado. No lo des por activo ni por bloqueado.';

/** Lo que el operador lee cuando el corte debía caer y el proveedor dice que no cayó. */
export const CUTOFF_REFUTED_NOTE =
    'El corte debía caer y el proveedor dice que el equipo sigue sin candado. Solo una persona puede resolverlo: revisa la bitácora y escálalo.';

/**
 * Desde cuándo está puesto un candado confirmado: la hora del corte si ya pasó y no es anterior a
 * la orden. `undefined` cuando solo se conoce la hora de la orden.
 */
export function lockFellAt(
    device: Pick<MdmFleetDevice, 'lockedAt' | 'currentLockScheduledFor'>,
    now: Date | string,
): string | undefined {
    const fallsAt = cutoffMs(device.currentLockScheduledFor);
    if (Number.isNaN(fallsAt) || fallsAt > new Date(now).getTime()) return undefined;
    const orderedAt = device.lockedAt ? new Date(device.lockedAt).getTime() : Number.NaN;
    if (!Number.isNaN(orderedAt) && fallsAt < orderedAt) return undefined;
    return device.currentLockScheduledFor;
}

/** Lo que el operador tiene que leer cuando el proveedor desmintió el corte de este equipo. */
export const LOCK_REFUTED_NOTE =
    'El proveedor desmintió el candado: el equipo quedó SIN candado aunque el expediente diga bloqueado. Solo una persona puede resolverlo: revisa la bitácora y escálalo.';

/**
 * Qué mide la «última señal» en cada proveedor. Datacultr: última acción aplicada; Trustonic:
 * check-in periódico; Motosafe no la reporta.
 */
export const MDM_HEARTBEAT_MEANING: Record<MdmProviderEnum, string> = {
    [MdmProviderEnum.DATACULTR]:
        'En Datacultr es la última acción que el equipo aplicó, no conectividad: un teléfono prendido y sin órdenes se ve sin señal, y un aviso entregado no la mueve.',
    [MdmProviderEnum.TRUSTONIC]:
        'En Trustonic es el check-in periódico del equipo. Se mueve al aplicar una acción, no al entregar un aviso.',
    [MdmProviderEnum.MOTOSAFE]:
        'Motosafe no reporta la última señal del equipo: en sus teléfonos este dato queda vacío.',
};

/** La columna mezcla equipos de varios proveedores, así que su ayuda nombra cada significado. */
export const MDM_HEARTBEAT_COLUMN_HELP = `Significa cosas distintas según el proveedor. ${MDM_HEARTBEAT_MEANING[MdmProviderEnum.DATACULTR]} ${MDM_HEARTBEAT_MEANING[MdmProviderEnum.TRUSTONIC]} ${MDM_HEARTBEAT_MEANING[MdmProviderEnum.MOTOSAFE]}`;

/** Lo que lleva abierta una pendiente para que la ficha la llame «estancada» y no «en vuelo». */
export const STALE_OPERATION_MS = 24 * 60 * 60 * 1000;

/** El proveedor arranca en cero aunque no tenga un solo equipo: cero es un dato, no un hueco. */
function emptyProviderCount(): Record<MdmProviderEnum, number> {
    return {
        [MdmProviderEnum.TRUSTONIC]: 0,
        [MdmProviderEnum.DATACULTR]: 0,
        [MdmProviderEnum.MOTOSAFE]: 0,
    };
}

/**
 * Cuenta la flota ENTERA, no la filtrada: la barra dice cuántos equipos hay y de quién son aunque
 * el filtro no deje ninguno. `now` entra por parámetro, igual que en `readHeartbeat`.
 */
export function summarizeFleet(
    devices: readonly MdmFleetDevice[],
    now: Date | string,
): MdmFleetSummary {
    const summary: MdmFleetSummary = {
        total: devices.length,
        byProvider: emptyProviderCount(),
        active: 0,
        activeUnconfirmed: 0,
        locked: 0,
        lockOrdersByPhase: {
            scheduled: 0,
            unconfirmed: 0,
            refuted: 0,
            cutoffPassed: 0,
            cutoffRefuted: 0,
        },
        enrolling: 0,
        needsAttention: 0,
        silent: 0,
        neverReported: 0,
        pendingOperations: 0,
        devicesWithPendingOperations: 0,
    };

    for (const device of devices) {
        summary.byProvider[device.mdmProvider] += 1;

        const state = readFleetState(device, now);
        if (state === 'activeUnconfirmed') summary.activeUnconfirmed += 1;
        else if (state === LoanDeviceMdmStatusEnum.ACTIVE) summary.active += 1;
        else if (state === 'locked') summary.locked += 1;
        else if (isLockPhase(state)) summary.lockOrdersByPhase[state] += 1;
        if (device.mdmStatus === LoanDeviceMdmStatusEnum.ENROLLING) summary.enrolling += 1;
        if (needsPersonAttention(device)) summary.needsAttention += 1;

        const heartbeat = readHeartbeat(device.lastSeenAt, now, device.mdmProvider);
        if (heartbeat.bucket === 'critical') summary.silent += 1;
        if (heartbeat.bucket === 'unknown') summary.neverReported += 1;

        if (device.pendingOperations > 0) {
            summary.pendingOperations += device.pendingOperations;
            summary.devicesWithPendingOperations += 1;
        }
    }

    return summary;
}

/**
 * Por qué la tabla salió vacía. Una flota sin un solo equipo NO es un filtro que no encontró nada;
 * `wholeFleet` en `false` = no se leyó la flota entera, así que no se puede afirmar que esté vacía.
 */
export function explainEmptyFleet(summary: MdmFleetSummary, wholeFleet: boolean): string {
    return wholeFleet && summary.total === 0
        ? 'Todavía no hay dispositivos enrolados en la flota.'
        : 'Sin dispositivos con estos filtros';
}
